/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import cn from 'classnames';
import { callAPI, setAuthCookie, userErrorMessage, showErrorToast, refreshPendingTickets } from 'helpers';
import Cookies from 'js-cookie';
import moment from 'moment';
import 'moment/locale/th';
import { useIdleTimer } from 'react-idle-timer';

import menus from 'nav/menus';
import Logo from 'nav/logo';

// builder ทั้งหน้า create และหน้า edit (24-hex id) — ใช้ทั้งซ่อน nav และยกเว้น idle-kick
// (เดิม onIdle เช็คเฉพาะ edit → หน้า create โดนเตะตอน idle 20 นาที งานที่ยังไม่บันทึกหายทั้งแฟ้ม)
const FOLIO_BUILDER_RE = /^\/profile\/folios\/(create|[0-9a-fA-F]{24})$/;

const IDLE_MS = 1000 * 60 * 20;
// เตะออกแบบเดียวกับ onIdle หน้าปกติ — ใช้ร่วมกับตอนออกจาก builder ทั้งที่เกินเวลา idle แล้ว
const kickToHome = () => {
  Cookies.remove('accessToken');
  Cookies.remove('email_timer_ends_at');
  Cookies.remove('mobile_timer_ends_at');
  window.location.pathname = '/';
};

const Profile = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Folio Builder owns the whole viewport (sticky topbar + 100vh layout) —
  // the fixed site nav and Logo banner would overlap it, so hide both there.
  const isFolioBuilder = FOLIO_BUILDER_RE.test(pathname);
  const dispatch = useDispatch();

  // ไม่มีระบบแปลภาษาแล้ว — ไทยอย่างเดียว (html lang="th" ตั้งใน public/index.html)
  useEffect(() => { moment.locale('th'); }, []);

  const accessToken = Cookies.get('accessToken');

  useIdleTimer({
    timeout: IDLE_MS,
    onIdle: () => {
      if (FOLIO_BUILDER_RE.test(window.location.pathname)) {
        // อ่าน cookie สด — ค่าที่ capture ตอน render อาจถูก logout จากแท็บอื่นไปแล้ว (เดิมเขียน token เก่ากลับ = session ฟื้น)
        const token = Cookies.get('accessToken');
        if (token) setAuthCookie('accessToken', token, 1 / (24 * 3));
      } else {
        kickToHome();
      }
    },
    onAction: () => {
      const accessToken = Cookies.get('accessToken');
      if (accessToken) {
        setAuthCookie('accessToken', accessToken, 1 / (24 * 3));
        // marker กิจกรรมจริงล่าสุด — keep-alive/onIdle ของ builder ต่ออายุแต่ accessToken
        // ไม่แตะ marker นี้ จึงใช้แยก "ยังใช้งานอยู่" ออกจาก "cookie สดเพราะ builder ต่อให้"
        setAuthCookie('last_action_at', String(Date.now()), 1);
      }
    },
    debounce: 500,
  });

  // builder เปิดค้างนาน: onIdle fire ครั้งเดียวต่อรอบ idle → ต่ออายุ cookie ได้แค่ 20 นาทีเดียว
  // แล้ว cookie หมดอายุเงียบๆ (กดบันทึกเจอ 401) — ต่ออายุเป็น interval ตลอดที่ยังเปิด builder
  // ทุก 10 นาที < อายุ cookie 20 นาที จึงไม่มีช่องให้หลุด (server token 24 ชม. เป็นเพดานจริง)
  useEffect(() => {
    if (!isFolioBuilder) return undefined;
    const keepAlive = setInterval(() => {
      const token = Cookies.get('accessToken');
      if (token) setAuthCookie('accessToken', token, 1 / (24 * 3));
    }, 1000 * 60 * 10);
    return () => clearInterval(keepAlive);
  }, [isFolioBuilder]);

  // การยกเว้น idle-kick ของ builder ต้องไม่รั่วออกนอก builder: ถ้าออกมาหน้าอื่นทั้งที่
  // เงียบเกิน 20 นาทีแล้ว (marker กิจกรรมจริงจาก onAction — ไม่ใช่ cookie ที่ keep-alive ต่อให้)
  // → เตะออกทันที · ถ้ายังใช้งานอยู่ (marker สด) ออกมาได้ตามปกติ
  // เช็คเฉพาะขาออกจาก builder (ref จำสถานะก่อนหน้า) — mount ปกติ/login ใหม่ไม่โดน marker ค้างจาก session ก่อน
  const wasBuilderRef = useRef(false);
  useEffect(() => {
    if (isFolioBuilder) {
      wasBuilderRef.current = true;
      // เข้า builder = กิจกรรม (กัน race กับ onAction ที่ debounce 500ms — เช่น deep-link เข้า builder ตรงๆ)
      setAuthCookie('last_action_at', String(Date.now()), 1);
      return;
    }
    if (!wasBuilderRef.current) return;
    wasBuilderRef.current = false;
    const last = Number(Cookies.get('last_action_at'));
    if (last && Date.now() - last > IDLE_MS) kickToHome();
  }, [isFolioBuilder]);

  const [verifying, setVerifying] = useState(true);
  // ยืนยันตัวตนรอบแรกผ่านแล้ว = ปล่อยให้หน้าลูกอยู่ต่อได้ตลอด — ห้ามผูกการ mount ของ <Outlet/>
  // ไว้กับ `verifying` ตรงๆ เพราะ `navigate` จาก useNavigate (react-router 6.x, non-data router)
  // เปลี่ยน identity ทุกครั้งที่ pathname เปลี่ยน → effect นี้ re-run ทุกการนำทาง → verifying
  // true→false → หน้าลูกถูก unmount แล้ว mount ใหม่ = ยิง API ซ้ำทั้งชุด และ state ที่ผู้ใช้
  // พิมพ์ค้างใน Folio Builder หายไปกับ instance เก่า (เคสเซฟทับด้วยข้อมูลเก่า 2026-08-24)
  const [bootstrapped, setBootstrapped] = useState(false);
  useEffect(() => {
    const verify = async () => {
      try {
        setVerifying(true);
        const { result } = await callAPI({ url: '/applicants/me' });
        dispatch({ type: 'UPDATE_USER', result });
        Cookies.remove('sessionToken');
        setVerifying(false);
        setBootstrapped(true);
      } catch (error) {
        console.error(new Error(`Error while trying to verify user: ${error.message}`));
        setVerifying(false);
        navigate('/');
      }
    };

    if (!accessToken) {
      navigate('/');
    } else {
      Cookies.remove('sessionToken');
      verify();
    }
  }, [dispatch, navigate, accessToken]);

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // courses_round1 ไม่ fetch ตรงนี้แล้ว — ทุก consumer ผ่าน useCoursesR1
        // (lazy + dedup ใน redux) ตัด fetch ซ้ำตอนเข้า /profile
        await refreshPendingTickets(dispatch);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  const [loggingOut, setLoggingOut] = useState(false);
  const onLogout = async () => {
    if (loggingOut) return;
    try {
      setLoggingOut(true);
      await callAPI({
        method: 'POST',
        url: '/applicants/logout',
      });
    } catch (error) {
      // server ล้ม/token หมดอายุแล้ว — ยังต้องล้าง session ในเครื่อง (เดิมค้างอยู่ในระบบบนเครื่องสาธารณะ)
      console.error(new Error(`Error while trying to logout: ${error.message}`));
      if (error && error.statusCode !== 401) showErrorToast(userErrorMessage(error));
    } finally {
      setLoggingOut(false);
      Cookies.remove('accessToken');
      Cookies.remove('email_timer_ends_at');
      Cookies.remove('mobile_timer_ends_at');
      navigate('/');
    }
  };

  useEffect(() => {
    const classNames = document.getElementById('root').className.split(' ');
    if (loading) {
      document.getElementById('root').className = _.uniq(classNames.concat(['is-loading'])).join(' ');
    } else {
      document.getElementById('root').className = classNames.filter((name) => name !== 'is-loading').join(' ');
    }
  }, [loading]);

  useEffect(() => {
    const classNames = document.getElementById('root').className.split(' ');
    if (verifying) {
      document.getElementById('root').className = _.uniq(classNames.concat(['is-loading'])).join(' ');
    } else {
      document.getElementById('root').className = classNames.filter((name) => name !== 'is-loading').join(' ');
    }
  }, [verifying]);

  let current = window.location.pathname.split('/')[2];
  if (current === undefined) {
    current = 'profile';
  }

  return (
    <div id="tcas">
      {loading && <div className="t-loading" />}

      {!isFolioBuilder && <>
      <header className="site-header">
        <nav className="site-nav -member">
          <ul>
            {menus.map(({ key, icon, title, to, href }) => (
              <li key={key} className={cn(`m-${key}`, { active: key === 'profile' })}>
                {to && (
                  <Link to={to}>
                    {icon}
                    <span>{(title)}</span>
                  </Link>
                )}
                {href && (
                  <a href={href}>
                    {icon}
                    <span>{(title)}</span>
                  </a>
                )}
              </li>
            ))}
            <li>
              <Link
                to="/profile/applicant-info"
                className={cn('m-applicant-info', { active: current === 'applicant-info' })}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM12 6.5C13.3805 6.5 14.5 7.6195 14.5 9C14.5 10.3805 13.3805 11.5 12 11.5C10.6195 11.5 9.5 10.3805 9.5 9C9.5 7.6195 10.6195 6.5 12 6.5ZM16.5 14.769C16.5 16.1985 14.6765 17.5 12 17.5C9.3235 17.5 7.5 16.1985 7.5 14.769V14.431C7.5 13.917 7.917 13.5 8.431 13.5H15.569C16.083 13.5 16.5 13.917 16.5 14.431V14.769Z"
                    fill="currentColor"
                  />
                </svg>
                <span>ข้อมูลส่วนตัว</span>
              </Link>
            </li>
            <li>
              <Link to="/profile" className={cn('m-profile', { active: current === 'profile' })}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M5.66667 1C4.74167 1 4 1.74167 4 2.66667V21C4 21.9167 4.74167 22.6667 5.66667 22.6667H19C19.9167 22.6667 20.6667 21.9167 20.6667 21V16.1416L17.6995 19.099C17.4162 19.3823 17.0577 19.5739 16.666 19.6572L15.0091 19.9909L14.9505 20.0495C14.3755 20.6245 13.4422 20.6245 12.8672 20.0495C12.2922 19.4745 12.2922 18.5411 12.8672 17.9661L13.1748 17.6569V17.0238C13.1748 16.3654 13.4422 15.7245 13.9089 15.2578L20.1328 9.04036C20.2911 8.88203 20.475 8.74993 20.6667 8.6416V7.25C20.6667 7.025 20.5758 6.81588 20.4258 6.65755L15.0091 1.24089C14.8508 1.08255 14.6333 1 14.4167 1H5.66667ZM14 2.58203L19.0749 7.66667H14.8333C14.375 7.66667 14 7.29167 14 6.83333V2.58203ZM22.9307 9.32682C22.8663 9.3332 22.8023 9.34902 22.7419 9.37402H22.7337C22.6504 9.40736 22.5756 9.45729 22.5173 9.51562C22.5006 9.53229 22.4833 9.54941 22.4749 9.56608C22.1083 9.37441 21.6425 9.44147 21.3258 9.75814L18.2253 12.8571L20.1328 14.7663L23.2334 11.6657C23.5417 11.349 23.6088 10.8818 23.4255 10.5068C23.4421 10.4985 23.4578 10.4909 23.4661 10.4743C23.5911 10.3576 23.658 10.1997 23.6663 10.0413C23.683 9.84967 23.6078 9.66562 23.4661 9.51562C23.3224 9.37187 23.1236 9.30768 22.9307 9.32682ZM6.50814 11.8333H12.3415C12.7998 11.8333 13.1748 12.2 13.1748 12.6667C13.1748 13.125 12.7998 13.5 12.3415 13.5H6.50814C6.0498 13.5 5.6748 13.125 5.6748 12.6667C5.6748 12.2 6.0498 11.8333 6.50814 11.8333ZM17.2666 13.8158L14.8838 16.1986L14.7747 16.3158C14.6997 16.4074 14.6911 16.4748 14.6494 16.6331C14.6411 16.6498 14.6415 16.657 14.6331 16.682L14.3662 17.6748C14.3495 17.7498 14.3418 17.8161 14.3418 17.8994C14.3418 17.9661 14.3495 18.0327 14.3662 18.1077L14.0505 18.4235C13.8921 18.5735 13.884 18.8332 14.0423 18.9915C14.2007 19.1582 14.4502 19.1663 14.6169 18.9997C14.6252 18.9997 14.625 18.9999 14.625 18.9915L14.9424 18.6742C15.0174 18.6992 15.0841 18.7083 15.1507 18.7083C15.2507 18.7083 15.3341 18.6912 15.4258 18.6579L16.3584 18.4154C16.3834 18.407 16.392 18.3991 16.417 18.3991C16.4837 18.3741 16.5334 18.349 16.5667 18.3324C16.6334 18.2824 16.6747 18.2244 16.7913 18.1077L19.1758 15.7249L17.2666 13.8158ZM6.50814 15.1569H9.84147C10.2998 15.1569 10.6748 15.5319 10.6748 15.9902C10.6748 16.4486 10.2998 16.8236 9.84147 16.8236H6.50814C6.0498 16.8236 5.6748 16.4486 5.6748 15.9902C5.6748 15.5319 6.0498 15.1569 6.50814 15.1569Z"
                    fill="currentColor"
                  />
                </svg>
                <span>การสมัครสอบ</span>
              </Link>
            </li>
            <li>
              <a onClick={onLogout}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 2C6.48904 2 2 6.48904 2 12C2 17.511 6.48904 22 12 22C14.6772 22 17.1417 20.9435 18.9414 19.2217C19.0363 19.1309 19.1124 19.0223 19.1654 18.9021C19.2183 18.7819 19.247 18.6524 19.25 18.5211C19.2529 18.3898 19.2299 18.2592 19.1823 18.1368C19.1347 18.0143 19.0635 17.9025 18.9727 17.8076C18.8819 17.7127 18.7733 17.6366 18.6531 17.5836C18.5329 17.5307 18.4034 17.502 18.2721 17.4991C18.1408 17.4962 18.0102 17.5192 17.8878 17.5668C17.7653 17.6143 17.6535 17.6856 17.5586 17.7764C16.1223 19.1505 14.1508 20 12 20C7.56996 20 4 16.43 4 12C4 7.56996 7.56996 4 12 4C14.1743 4 16.1207 4.83116 17.5566 6.21875C17.6501 6.31442 17.7618 6.39037 17.8851 6.44211C18.0085 6.49385 18.1409 6.52032 18.2747 6.51997C18.4084 6.51962 18.5407 6.49245 18.6638 6.44006C18.7868 6.38768 18.8981 6.31114 18.9911 6.21498C19.084 6.11883 19.1567 6.005 19.2049 5.88025C19.2531 5.75549 19.2758 5.62233 19.2716 5.48865C19.2675 5.35498 19.2365 5.2235 19.1806 5.10199C19.1248 4.98048 19.0451 4.87142 18.9463 4.78125C17.1452 3.04084 14.6877 2 12 2ZM17.9893 7.99023C17.7904 7.99048 17.5961 8.05 17.4313 8.16119C17.2664 8.27238 17.1385 8.43018 17.0637 8.61446C16.989 8.79873 16.9709 9.00109 17.0117 9.19571C17.0525 9.39032 17.1505 9.56834 17.293 9.70703L18.5859 11H9.5C9.36749 10.9981 9.23593 11.0226 9.11296 11.072C8.98999 11.1214 8.87807 11.1948 8.7837 11.2878C8.68933 11.3809 8.61439 11.4918 8.56324 11.614C8.5121 11.7363 8.48576 11.8675 8.48576 12C8.48576 12.1325 8.5121 12.2637 8.56324 12.386C8.61439 12.5082 8.68933 12.6191 8.7837 12.7122C8.87807 12.8052 8.98999 12.8786 9.11296 12.928C9.23593 12.9774 9.36749 13.0019 9.5 13H18.5859L17.293 14.293C17.197 14.3851 17.1204 14.4955 17.0676 14.6176C17.0148 14.7397 16.9869 14.8712 16.9856 15.0042C16.9842 15.1373 17.0094 15.2692 17.0597 15.3924C17.11 15.5156 17.1844 15.6275 17.2784 15.7216C17.3725 15.8156 17.4844 15.89 17.6076 15.9403C17.7308 15.9906 17.8627 16.0158 17.9958 16.0144C18.1288 16.0131 18.2603 15.9852 18.3824 15.9324C18.5045 15.8796 18.6149 15.803 18.707 15.707L21.707 12.707C21.8945 12.5195 21.9998 12.2652 21.9998 12C21.9998 11.7348 21.8945 11.4805 21.707 11.293L18.707 8.29297C18.6137 8.19706 18.5021 8.12084 18.3788 8.06884C18.2555 8.01684 18.1231 7.99011 17.9893 7.99023Z"
                    fill="currentColor"
                  />
                </svg>
                <span>ออกจากระบบ</span>
              </a>
            </li>
          </ul>
        </nav>
      </header>
      <Logo />
      </>}
      {!loading && bootstrapped && <Outlet />}
    </div>
  );
};

export default Profile;
